/* Shared collection and campaign product-card slider. */
(() => {
  if (window.cherryCardSlider) return;
  const selector = '[data-cherry-collection-slider]';
  const instances = new Map();
  const brightnessCache = new Map();

  const getSlideImage = slide => {
    if (!slide) return null;
    const img = slide.querySelector('img:not([aria-hidden="true"])') || slide.querySelector('img');
    if (img) {
      const src = img.currentSrc || img.src || img.getAttribute('data-src') || img.getAttribute('data-srcset');
      if (src) return { el: img, src };
    }
    const bgEl = slide.querySelector('[style*="background-image"]') || slide;
    const style = bgEl.style.backgroundImage || (window.getComputedStyle && window.getComputedStyle(bgEl).backgroundImage);
    if (style && style !== 'none') {
      const match = style.match(/url\(["']?([^"')]+)["']?\)/);
      if (match && match[1] && !match[1].startsWith('data:')) {
        return { el: null, src: match[1] };
      }
    }
    const bgsetEl = slide.querySelector('[data-bgset]');
    if (bgsetEl) {
      const bgset = bgsetEl.getAttribute('data-bgset');
      if (bgset) {
        const first = bgset.split(',')[0]?.trim()?.split(' ')[0];
        if (first) return { el: null, src: first };
      }
    }
    return null;
  };

  const updateContrast = (slider, flkty) => {
    const card = slider.closest('[data-product-media-container], .product-grid-item, .cherry-product-card, article');
    const arrows = card?.querySelector('.cherry-collection-card__arrows');
    if (!arrows) return;

    const activeCell = flkty.selectedElement || flkty.cells?.[flkty.selectedIndex]?.element || slider.querySelector('[data-product-media-slideshow-slide]');
    const imageInfo = getSlideImage(activeCell);
    if (!imageInfo || !imageInfo.src) {
      arrows.classList.add('is-light-bg');
      arrows.classList.remove('is-dark-bg');
      return;
    }

    const { el: imgEl, src } = imageInfo;

    if (brightnessCache.has(src)) {
      const isDark = brightnessCache.get(src);
      arrows.classList.toggle('is-dark-bg', isDark);
      arrows.classList.toggle('is-light-bg', !isDark);
      return;
    }

    const sample = imageObj => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 16;
        canvas.height = 16;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(imageObj, 0, 0, 16, 16);
        const data = ctx.getImageData(0, 0, 16, 16).data;
        let sum = 0;
        for (let i = 0; i < data.length; i += 4) {
          sum += (data[i] * 299 + data[i + 1] * 587 + data[i + 2] * 114) / 1000;
        }
        const isDark = (sum / (data.length / 4)) < 130;
        brightnessCache.set(src, isDark);
        arrows.classList.toggle('is-dark-bg', isDark);
        arrows.classList.toggle('is-light-bg', !isDark);
      } catch (_) {
        arrows.classList.add('is-light-bg');
        arrows.classList.remove('is-dark-bg');
      }
    };

    if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
      sample(imgEl);
    } else if (imgEl) {
      imgEl.addEventListener('load', () => sample(imgEl), { once: true });
      const loader = new Image();
      loader.crossOrigin = 'anonymous';
      loader.onload = () => sample(loader);
      loader.src = src;
    } else {
      const loader = new Image();
      loader.crossOrigin = 'anonymous';
      loader.onload = () => sample(loader);
      loader.src = src;
    }
  };

  const destroy = slider => {
    const instance = instances.get(slider);
    if (!instance) return;
    instance.observer.disconnect();
    instance.flkty.destroy();
    instances.delete(slider);
  };

  const init = (root = document) => {
    const Flickity = (window.themeVendor && window.themeVendor.Flickity) || window.Flickity;
    if (!Flickity) {
      setTimeout(() => init(root), 50);
      return;
    }
    instances.forEach((instance, slider) => { if (!slider.isConnected) destroy(slider); });
    root.querySelectorAll(selector).forEach(slider => {
      if (instances.has(slider)) return;
      const cellCount = slider.querySelectorAll('[data-product-media-slideshow-slide]').length;
      if (cellCount < 2) return;
      const flkty = Flickity.data(slider) || new Flickity(slider, {
        cellSelector: '[data-product-media-slideshow-slide]',
        contain: true,
        wrapAround: true,
        imagesLoaded: true,
        lazyLoad: true,
        pageDots: false,
        prevNextButtons: false,
        adaptiveHeight: false,
        draggable: true,
        selectedAttraction: 0.2,
        friction: 1
      });
      const progress = slider.closest('[data-product-media-container]')?.querySelector('[data-collection-card-progress]');
      const sync = () => {
        if (progress) {
          const availableTravel = Math.max(progress.parentElement.clientWidth - progress.offsetWidth, 0);
          progress.style.transform = `translateX(${availableTravel * flkty.selectedIndex / (cellCount - 1)}px)`;
        }
        slider.querySelectorAll('a[data-product-media-slideshow-slide]').forEach((link, index) => {
          link.tabIndex = index === flkty.selectedIndex ? 0 : -1;
        });
        updateContrast(slider, flkty);
      };

      flkty.on('change', sync);
      flkty.on('select', sync);
      flkty.on('settle', sync);
      const observer = new ResizeObserver(sync);
      observer.observe(slider);
      instances.set(slider, { flkty, observer });

      const card = slider.closest('[data-product-media-container], .product-grid-item, .cherry-product-card, article');
      if (card) {
        card.addEventListener('mouseenter', sync, { passive: true });
      }

      sync();
      requestAnimationFrame(sync);
      setTimeout(sync, 150);
      setTimeout(sync, 500);
    });
  };

  window.cherryCardSlider = { init };
  document.addEventListener('click', event => {
    const arrow = event.target.closest('[data-collection-slide-previous], [data-collection-slide-next]');
    if (!arrow) return;
    const slider = arrow.closest('[data-product-media-container], .product-grid-item, .cherry-product-card, article')?.querySelector(selector);
    const Flickity = (window.themeVendor && window.themeVendor.Flickity) || window.Flickity;
    const flkty = slider && Flickity?.data(slider);
    if (!flkty) return;
    event.preventDefault();
    event.stopPropagation();
    if (arrow.hasAttribute('data-collection-slide-previous')) flkty.previous(true);
    else flkty.next(true);
  });
  const initialize = () => requestAnimationFrame(() => init());
  let mutationFrame = null;
  const addedProductObserver = new MutationObserver(mutations => {
    const hasNewSlider = mutations.some(mutation =>
      Array.from(mutation.addedNodes).some(node =>
        node.nodeType === 1 && (node.matches?.(selector) || node.querySelector?.(selector))
      )
    );

    if (!hasNewSlider || mutationFrame !== null) return;
    mutationFrame = requestAnimationFrame(() => {
      mutationFrame = null;
      init();
    });
  });
  const observeAddedProducts = () => {
    if (!document.body) return;
    addedProductObserver.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initialize();
      observeAddedProducts();
    }, { once: true });
  } else {
    initialize();
    observeAddedProducts();
  }
  window.addEventListener('load', initialize);
  document.addEventListener('theme:filters:init', initialize);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll(selector).forEach(destroy);
  });
})();
